import { TFunction } from 'i18next';
import {
  BuyerAudience,
  ClinicProduct,
  ClinicStockStatus,
  HerbOrigin,
  ProductCategory,
  ProductInfoSectionKey,
  ProductTag,
  ProductType,
  VirtualProductFormat,
} from '../../types';
import { StatusTone } from '../common';

export const stockStatusTone = (status: ClinicStockStatus): StatusTone => {
  switch (status) {
    case 'IN_STOCK':
      return 'success';
    case 'LOW_STOCK':
      return 'warning';
    case 'OUT_OF_STOCK':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const stockStatusLabel = (
  status: ClinicStockStatus,
  t: TFunction,
): string => {
  switch (status) {
    case 'IN_STOCK':
      return t('STOCK_IN_STOCK');
    case 'LOW_STOCK':
      return t('STOCK_LOW_STOCK');
    case 'OUT_OF_STOCK':
      return t('STOCK_OUT_OF_STOCK');
    default:
      return status;
  }
};

/**
 * Single source of truth for the stock/availability pill shown on product
 * surfaces (catalog card, catalog row, product detail). Returns the localized
 * label and a `StatusTone` — render with `<StatusBadge tone=... label=... />`
 * so the SAME status looks identical on every page.
 *
 * Covers both physical SKUs (IN_STOCK / LOW_STOCK / OUT_OF_STOCK) and virtual
 * SKUs (sold-out / few-slots-left / available / unlimited).
 */
export const stockPill = (
  product: ClinicProduct,
  t: TFunction,
): { label: string; tone: StatusTone } => {
  if (product.productType === 'VIRTUAL') {
    const vc = product.virtualConfig;
    if (vc?.capacity === null) {
      return { label: t('VIRTUAL_UNLIMITED_SLOTS'), tone: 'success' };
    }
    const remaining = virtualRemainingSlots(product);
    if (remaining <= 0) return { label: t('VIRTUAL_SOLD_OUT'), tone: 'danger' };
    if (remaining < 10) {
      return { label: t('VIRTUAL_SLOTS_LEFT', { count: remaining }), tone: 'warning' };
    }
    return {
      label: t('VIRTUAL_SLOTS_AVAILABLE', { count: remaining }),
      tone: 'success',
    };
  }
  if (product.stockStatus === 'OUT_OF_STOCK') {
    return { label: t('STOCK_OUT_OF_STOCK'), tone: 'danger' };
  }
  if (product.stockStatus === 'LOW_STOCK') {
    return {
      label: t('PRODUCT_ONLY_N_LEFT', { count: product.availableStock }),
      tone: 'warning',
    };
  }
  return { label: t('STOCK_IN_STOCK'), tone: 'success' };
};

export const categoryLabel = (
  category: ProductCategory,
  t: TFunction,
): string => {
  switch (category) {
    case 'MEDICINE':
      return t('CATEGORY_MEDICINE');
    case 'CHINESE_HERB':
      return t('CATEGORY_CHINESE_HERB');
    case 'MEDICAL_SUPPLY':
      return t('CATEGORY_MEDICAL_SUPPLY');
    case 'CONSUMABLE':
      return t('CATEGORY_CONSUMABLE');
    case 'EQUIPMENT':
      return t('CATEGORY_EQUIPMENT');
    case 'OTHER':
      return t('CATEGORY_OTHER');
    default:
      return category;
  }
};

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  'MEDICINE',
  'CHINESE_HERB',
  'MEDICAL_SUPPLY',
  'CONSUMABLE',
  'EQUIPMENT',
  'OTHER',
];

export const CLINIC_STOCK_STATUSES: ClinicStockStatus[] = [
  'IN_STOCK',
  'LOW_STOCK',
  'OUT_OF_STOCK',
];

export const formatPrice = (value: number, currency = 'AUD'): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);

export type BuyerIdentity = 'BUSINESS' | 'INDIVIDUAL' | 'GUEST';

/**
 * Catalogue price for the buyer's identity: Business → `businessPrice`,
 * Individual / Guest → `individualPrice`. Returns `null` when the applicable
 * price is missing or `0`, signalling the caller to show "contact platform"
 * and block purchase.
 */
/**
 * `true` when this product's `buyerAudience` is sold to the current identity.
 * Guests are treated as Individuals (same pricing track). Missing audience
 * defaults to `BOTH` (back-compat with pre-V2.2 products).
 */
export const isAudienceVisible = (
  audience: BuyerAudience | undefined,
  identity: BuyerIdentity,
): boolean => {
  const a = audience ?? 'BOTH';
  // BOTH and ALL both mean "visible to every identity".
  if (a === 'BOTH' || a === 'ALL') return true;
  if (identity === 'BUSINESS') return a === 'BUSINESS_ONLY';
  // INDIVIDUAL and GUEST share the Individual track.
  return a === 'INDIVIDUAL_ONLY';
};

export const resolveProductPrice = (
  product: Pick<ClinicProduct, 'businessPrice' | 'individualPrice'>,
  identity: BuyerIdentity,
): number | null => {
  const raw = identity === 'BUSINESS' ? product.businessPrice : product.individualPrice;
  return typeof raw === 'number' && raw > 0 ? raw : null;
};

export const HERB_ORIGINS: HerbOrigin[] = [
  'ROOT_RHIZOME',
  'LEAVES_FLOWERS',
  'FRUITS_SEEDS',
  'BARK_RESIN',
  'ANIMAL',
  'MINERAL',
  'WHOLE_HERB',
];

export const herbOriginLabel = (origin: HerbOrigin, t: TFunction): string => {
  switch (origin) {
    case 'ROOT_RHIZOME':
      return t('HERB_ORIGIN_ROOT_RHIZOME');
    case 'LEAVES_FLOWERS':
      return t('HERB_ORIGIN_LEAVES_FLOWERS');
    case 'FRUITS_SEEDS':
      return t('HERB_ORIGIN_FRUITS_SEEDS');
    case 'BARK_RESIN':
      return t('HERB_ORIGIN_BARK_RESIN');
    case 'ANIMAL':
      return t('HERB_ORIGIN_ANIMAL');
    case 'MINERAL':
      return t('HERB_ORIGIN_MINERAL');
    case 'WHOLE_HERB':
      return t('HERB_ORIGIN_WHOLE_HERB');
    default:
      return origin;
  }
};

export const tagLabel = (tag: ProductTag, t: TFunction): string => {
  switch (tag) {
    case 'SPECIAL':
      return t('TAG_SPECIAL');
    case 'FREQUENT':
      return t('TAG_FREQUENT');
    default:
      return tag;
  }
};

export type TagPalette = { bg: string; fg: string };

export const tagPalette = (tag: ProductTag): TagPalette => {
  switch (tag) {
    case 'SPECIAL':
      return { bg: '#16a34a', fg: '#ffffff' };
    case 'FREQUENT':
      return { bg: '#3b82f6', fg: '#ffffff' };
    default:
      return { bg: '#9ca3af', fg: '#ffffff' };
  }
};

export const productTypeLabel = (type: ProductType, t: TFunction): string =>
  type === 'VIRTUAL' ? t('PRODUCT_TYPE_VIRTUAL') : t('PRODUCT_TYPE_PHYSICAL');

export const virtualFormatLabel = (
  format: VirtualProductFormat,
  t: TFunction,
): string => {
  switch (format) {
    case 'ONLINE_COURSE':
      return t('VIRTUAL_FORMAT_ONLINE_COURSE');
    case 'OFFLINE_COURSE':
      return t('VIRTUAL_FORMAT_OFFLINE_COURSE');
    case 'OTHER':
      return t('VIRTUAL_FORMAT_OTHER');
    default:
      return format;
  }
};

/** `true` when the virtual product has no capacity cap (backend `capacity = null`). */
export const virtualIsUnlimited = (product: ClinicProduct): boolean =>
  product.productType === 'VIRTUAL' && product.virtualConfig?.capacity === null;

/** Returns `Infinity` for unlimited-capacity virtual products. */
export const virtualRemainingSlots = (product: ClinicProduct): number => {
  const vc = product.virtualConfig;
  if (!vc) return 0;
  if (vc.capacity === null) return Number.POSITIVE_INFINITY;
  return Math.max(0, vc.capacity - vc.reservedSlots - vc.soldSlots);
};

export const virtualIsAvailable = (product: ClinicProduct): boolean => {
  if (product.productType !== 'VIRTUAL') return true;
  const at = product.virtualConfig?.autoDelistAt;
  if (at && Date.parse(at) <= Date.now()) return false;
  if (virtualRemainingSlots(product) <= 0) return false;
  return true;
};

/**
 * `true` when a virtual product's `autoDelistAt` has passed. Out-of-stock
 * physical products and zero-slots virtual products are NOT considered delisted —
 * those should still render in catalogue listings.
 */
export const isDelisted = (product: ClinicProduct): boolean => {
  if (product.productType !== 'VIRTUAL') return false;
  const at = product.virtualConfig?.autoDelistAt;
  return !!at && Date.parse(at) <= Date.now();
};

export const daysUntil = (iso?: string): number | null => {
  if (!iso) return null;
  const diff = Date.parse(iso) - Date.now();
  if (Number.isNaN(diff)) return null;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const isOutOfStock = (product: ClinicProduct): boolean => {
  if (product.productType === 'VIRTUAL') return !virtualIsAvailable(product);
  return product.stockStatus === 'OUT_OF_STOCK' || product.availableStock <= 0;
};

export const infoSectionTitle = (
  key: ProductInfoSectionKey,
  t: TFunction,
): string => {
  switch (key) {
    case 'GENERAL':
      return t('INFO_SECTION_GENERAL');
    case 'INGREDIENTS':
      return t('INFO_SECTION_INGREDIENTS');
    case 'DIRECTIONS':
      return t('INFO_SECTION_DIRECTIONS');
    case 'WARNINGS':
      return t('INFO_SECTION_WARNINGS');
    default:
      return key;
  }
};
